import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { SimpleModal } from '@/components/SimpleModal';
import { useToast } from '@/components/Toast';
import { api } from '@/lib/api';
import { BACKEND_URL } from '@/lib/backendUrl';
import { Edit, Trash2, Plus, Eye, FileText, Sparkles, Upload, Image as ImageIcon, X } from 'lucide-react';

const emptyPost = {
  id: null,
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  keywords: '',
  tags: '',
  meta_description: '',
  cover_image: '',
  author: 'Equipe nfcesysPdv',
  published: true,
  faqs: [],
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
};

export default function BlogAdminPanel() {
  const toast = useToast();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(emptyPost);
  const [saving, setSaving] = useState(false);

  // AI generator
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiForm, setAiForm] = useState({ topic: '', keywords: '', length: 'longo' });
  const [generating, setGenerating] = useState(false);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/blog');
      setPosts(data.posts || []);
    } catch (e) {
      toast.error('Erro ao carregar artigos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const openNew = () => {
    setEditing({ ...emptyPost });
    setShowModal(true);
  };

  const openEdit = (post) => {
    setEditing({
      ...post,
      keywords: (post.keywords || []).join(', '),
      tags: (post.tags || []).join(', '),
      cover_image: post.cover_image || '',
      faqs: post.faqs || [],
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!editing.title?.trim() || !editing.content?.trim()) {
      toast.warning('Título e conteúdo são obrigatórios');
      return;
    }
    setSaving(true);
    const payload = {
      title: editing.title.trim(),
      slug: editing.slug?.trim() || null,
      excerpt: editing.excerpt || '',
      content: editing.content,
      meta_description: editing.meta_description || '',
      keywords: (editing.keywords || '')
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean),
      tags: (editing.tags || '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      cover_image: editing.cover_image?.trim() || null,
      author: editing.author?.trim() || 'Equipe nfcesysPdv',
      published: !!editing.published,
      faqs: (editing.faqs || [])
        .filter((f) => f.question?.trim() && f.answer?.trim())
        .map((f) => ({ question: f.question.trim(), answer: f.answer.trim() })),
    };

    try {
      if (editing.id) {
        await api.put(`/admin/blog/${editing.id}`, payload);
        toast.success('Artigo atualizado');
      } else {
        await api.post('/admin/blog', payload);
        toast.success('Artigo criado');
      }
      setShowModal(false);
      fetchPosts();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erro ao salvar artigo';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Excluir o artigo "${post.title}"?`)) return;
    try {
      await api.delete(`/admin/blog/${post.id}`);
      toast.success('Artigo excluído');
      fetchPosts();
    } catch (e) {
      toast.error('Erro ao excluir artigo');
    }
  };

  const togglePublished = async (post) => {
    try {
      await api.put(`/admin/blog/${post.id}`, {
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt,
        content: post.content,
        meta_description: post.meta_description,
        keywords: post.keywords || [],
        tags: post.tags || [],
        cover_image: post.cover_image,
        author: post.author,
        published: !post.published,
      });
      fetchPosts();
    } catch (e) {
      toast.error('Erro ao alterar status');
    }
  };

  const handleGenerateAi = async () => {
    if (!aiForm.topic.trim()) {
      toast.warning('Informe o tópico');
      return;
    }
    setGenerating(true);
    try {
      const payload = {
        topic: aiForm.topic.trim(),
        keywords: aiForm.keywords
          .split(',')
          .map((k) => k.trim())
          .filter(Boolean),
        length: aiForm.length,
      };
      const { data } = await api.post('/admin/blog/generate', payload, {
        timeout: 120000,
      });
      const draft = data.draft || {};
      setEditing({
        id: null,
        title: draft.title || '',
        slug: draft.slug || '',
        excerpt: draft.excerpt || '',
        content: draft.content || '',
        keywords: (draft.keywords || []).join(', '),
        tags: (draft.tags || []).join(', '),
        meta_description: draft.meta_description || '',
        cover_image: draft.cover_image || '',
        author: 'Equipe nfcesysPdv',
        published: false, // start as draft for human review
        faqs: draft.faqs || [],
      });
      setShowAiModal(false);
      setShowModal(true);
      const warns = [];
      if (draft.cover_warning) warns.push('capa');
      if (draft.images_warning) warns.push('imagens inline');
      if (warns.length) {
        toast.warning(
          `Rascunho gerado, mas houve falha em: ${warns.join(', ')}. Você pode gerar/upar manualmente no editor.`
        );
      } else {
        toast.success('Rascunho gerado com imagens — revise antes de publicar');
      }
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        'Falha ao gerar artigo. Tente novamente em alguns segundos.';
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  // ===== Cover image: upload, AI-generate or paste URL =====
  const [uploadingCover, setUploadingCover] = useState(false);
  const [generatingCover, setGeneratingCover] = useState(false);

  const handleCoverUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione um arquivo de imagem');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Imagem maior que 8 MB');
      return;
    }
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/blog/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditing((e) => ({ ...e, cover_image: data.url }));
      toast.success('Imagem enviada');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha no upload');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleGenerateCover = async () => {
    const basePrompt = `Cover image for article titled "${editing.title || aiForm.topic}". Professional photograph, warm lighting, brazilian small business context, no text, no watermark.`;
    setGeneratingCover(true);
    try {
      const { data } = await api.post(
        '/admin/blog/generate-cover',
        { prompt: basePrompt, slug: editing.slug || 'cover' },
        { timeout: 90000 }
      );
      setEditing((e) => ({ ...e, cover_image: data.url }));
      toast.success('Capa gerada com IA');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao gerar capa');
    } finally {
      setGeneratingCover(false);
    }
  };

  // FAQ helpers
  const addFaq = () => {
    setEditing((e) => ({
      ...e,
      faqs: [...(e.faqs || []), { question: '', answer: '' }],
    }));
  };
  const updateFaq = (idx, field, value) => {
    setEditing((e) => {
      const faqs = [...(e.faqs || [])];
      faqs[idx] = { ...faqs[idx], [field]: value };
      return { ...e, faqs };
    });
  };
  const removeFaq = (idx) => {
    setEditing((e) => ({
      ...e,
      faqs: (e.faqs || []).filter((_, i) => i !== idx),
    }));
  };

  return (
    <div className="space-y-4" data-testid="blog-admin-panel">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Mini-Blog SEO
            </CardTitle>
            <CardDescription>
              Gerencie artigos públicos que aparecem em <code>/blog</code> e ajudam no
              ranqueamento orgânico do Google.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setAiForm({ topic: '', keywords: '', length: 'longo' });
                setShowAiModal(true);
              }}
              data-testid="blog-admin-generate-ai"
              className="border-amber-500/40 text-amber-600 hover:bg-amber-50 hover:text-amber-700"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Gerar com IA
            </Button>
            <Button onClick={openNew} data-testid="blog-admin-new-post">
              <Plus className="w-4 h-4 mr-2" />
              Novo artigo
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-slate-500">Carregando...</p>
          ) : posts.length === 0 ? (
            <p className="text-center py-8 text-slate-500">
              Nenhum artigo cadastrado. Clique em "Novo artigo" para começar.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Título</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Publicado</TableHead>
                  <TableHead>Views</TableHead>
                  <TableHead>Atualizado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map((post) => (
                  <TableRow key={post.id} data-testid={`blog-admin-row-${post.slug}`}>
                    <TableCell className="font-medium max-w-md truncate">
                      {post.title}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{post.slug}</TableCell>
                    <TableCell>
                      <Switch
                        checked={!!post.published}
                        onCheckedChange={() => togglePublished(post)}
                        data-testid={`blog-admin-toggle-${post.slug}`}
                      />
                    </TableCell>
                    <TableCell>{post.views || 0}</TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {formatDate(post.updated_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                          title="Ver no site"
                          data-testid={`blog-admin-preview-${post.slug}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(post)}
                          data-testid={`blog-admin-edit-${post.slug}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(post)}
                          data-testid={`blog-admin-delete-${post.slug}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Editor modal */}
      <SimpleModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editing.id ? 'Editar artigo' : 'Novo artigo'}
      >
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
          <div>
            <Label>Título *</Label>
            <Input
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              placeholder="Ex.: Automação comercial para pequenos negócios"
              data-testid="blog-admin-input-title"
            />
          </div>

          <div>
            <Label>Slug (URL) — deixe em branco para gerar automaticamente</Label>
            <Input
              value={editing.slug}
              onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
              placeholder="ex: automacao-comercial-pequenos-negocios"
              data-testid="blog-admin-input-slug"
            />
          </div>

          <div>
            <Label>Resumo (excerpt) — usado na listagem</Label>
            <textarea
              className="w-full min-h-[60px] rounded-md border border-input bg-background p-2 text-sm"
              value={editing.excerpt}
              onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
              data-testid="blog-admin-input-excerpt"
            />
          </div>

          <div>
            <Label>Meta description (SEO) — máx. 160 caracteres</Label>
            <Input
              value={editing.meta_description}
              maxLength={170}
              onChange={(e) =>
                setEditing({ ...editing, meta_description: e.target.value })
              }
              data-testid="blog-admin-input-meta"
            />
            <p className="text-xs text-slate-500 mt-1">
              {editing.meta_description?.length || 0}/160
            </p>
          </div>

          <div>
            <Label>Palavras-chave (separadas por vírgula)</Label>
            <Input
              value={editing.keywords}
              onChange={(e) => setEditing({ ...editing, keywords: e.target.value })}
              placeholder="automação comercial, PDV, sistema para loja"
              data-testid="blog-admin-input-keywords"
            />
          </div>

          <div>
            <Label>Tags (separadas por vírgula)</Label>
            <Input
              value={editing.tags}
              onChange={(e) => setEditing({ ...editing, tags: e.target.value })}
              placeholder="PDV, automação, gestão"
              data-testid="blog-admin-input-tags"
            />
          </div>

          <div>
            <Label>Imagem de capa</Label>
            <div className="flex items-center gap-2 flex-wrap">
              <label
                className="inline-flex items-center gap-2 cursor-pointer rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-slate-50"
                data-testid="blog-admin-cover-upload-label"
              >
                <Upload className="w-4 h-4" />
                {uploadingCover ? 'Enviando...' : 'Upload do PC'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadingCover}
                  onChange={(e) => handleCoverUpload(e.target.files?.[0])}
                  data-testid="blog-admin-cover-upload-input"
                />
              </label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleGenerateCover}
                disabled={generatingCover || (!editing.title && !aiForm.topic)}
                data-testid="blog-admin-cover-generate-ai"
                className="border-amber-500/40 text-amber-700 hover:bg-amber-50"
              >
                {generatingCover ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-2 animate-pulse" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Gerar com IA
                  </>
                )}
              </Button>
              {editing.cover_image && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing({ ...editing, cover_image: '' })}
                  data-testid="blog-admin-cover-clear"
                >
                  <X className="w-4 h-4 mr-1" /> Remover
                </Button>
              )}
            </div>
            <Input
              value={editing.cover_image}
              onChange={(e) =>
                setEditing({ ...editing, cover_image: e.target.value })
              }
              placeholder="Ou cole uma URL: https://... ou /api/uploads/blog/..."
              className="mt-2"
              data-testid="blog-admin-input-cover"
            />
            {editing.cover_image && (
              <div className="mt-2 border rounded-md p-2 bg-slate-50">
                <img
                  src={
                    editing.cover_image.startsWith('/')
                      ? `${BACKEND_URL}${editing.cover_image}`
                      : editing.cover_image
                  }
                  alt="Capa preview"
                  className="max-h-40 rounded"
                  data-testid="blog-admin-cover-preview"
                />
              </div>
            )}
          </div>

          <div>
            <Label>Conteúdo (HTML) *</Label>
            <textarea
              className="w-full min-h-[260px] rounded-md border border-input bg-background p-2 text-sm font-mono"
              value={editing.content}
              onChange={(e) => setEditing({ ...editing, content: e.target.value })}
              placeholder="<p>Use HTML simples: &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;a&gt;, &lt;strong&gt;...</p>"
              data-testid="blog-admin-input-content"
            />
            <p className="text-xs text-slate-500 mt-1">
              Use tags HTML simples: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;a href&gt;, &lt;img src&gt;.
            </p>
          </div>

          {/* FAQ editor — gera Schema FAQPage para Google Rich Results */}
          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base">Perguntas frequentes (FAQ Schema)</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addFaq}
                data-testid="blog-admin-faq-add"
              >
                <Plus className="w-4 h-4 mr-1" /> Adicionar pergunta
              </Button>
            </div>
            <p className="text-xs text-slate-500 mb-2">
              4-6 perguntas reais que o público busca no Google. Aparece em <strong>"Perguntas e Respostas"</strong> no resultado de busca (Google Rich Results) — aumenta o CTR.
            </p>
            <div className="space-y-2">
              {(editing.faqs || []).length === 0 && (
                <p className="text-sm italic text-slate-400">
                  Nenhuma pergunta adicionada. Clique em "Adicionar pergunta".
                </p>
              )}
              {(editing.faqs || []).map((faq, idx) => (
                <div
                  key={idx}
                  className="border rounded-md p-3 bg-slate-50 space-y-2"
                  data-testid={`blog-admin-faq-${idx}`}
                >
                  <div className="flex items-start gap-2">
                    <Input
                      value={faq.question || ''}
                      onChange={(e) => updateFaq(idx, 'question', e.target.value)}
                      placeholder="Pergunta (ex.: Quanto custa o sistema PDV?)"
                      data-testid={`blog-admin-faq-question-${idx}`}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeFaq(idx)}
                      data-testid={`blog-admin-faq-remove-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <textarea
                    className="w-full min-h-[70px] rounded-md border border-input bg-background p-2 text-sm"
                    value={faq.answer || ''}
                    onChange={(e) => updateFaq(idx, 'answer', e.target.value)}
                    placeholder="Resposta direta (2-4 frases)"
                    data-testid={`blog-admin-faq-answer-${idx}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Autor</Label>
            <Input
              value={editing.author}
              onChange={(e) => setEditing({ ...editing, author: e.target.value })}
              data-testid="blog-admin-input-author"
            />
          </div>

          <div className="flex items-center justify-between border-t pt-3">
            <div className="flex items-center gap-2">
              <Switch
                checked={!!editing.published}
                onCheckedChange={(v) => setEditing({ ...editing, published: v })}
                data-testid="blog-admin-input-published"
              />
              <Label>Publicado</Label>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowModal(false)}
                disabled={saving}
                data-testid="blog-admin-cancel"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="blog-admin-save"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      </SimpleModal>

      {/* AI generation modal */}
      <SimpleModal
        open={showAiModal}
        onClose={() => (generating ? null : setShowAiModal(false))}
        title="Gerar artigo com IA"
      >
        <div className="space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
            <p className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                A IA gera um <strong>rascunho</strong> otimizado para SEO. Você
                <strong> sempre revisa e ajusta</strong> antes de publicar. O artigo
                inicia como rascunho (não publicado).
              </span>
            </p>
          </div>

          <div>
            <Label>Tópico / título base *</Label>
            <Input
              value={aiForm.topic}
              onChange={(e) => setAiForm({ ...aiForm, topic: e.target.value })}
              placeholder="Ex.: Como escolher um sistema PDV para açougue"
              data-testid="blog-ai-input-topic"
              disabled={generating}
            />
          </div>

          <div>
            <Label>Palavras-chave principais (separadas por vírgula)</Label>
            <Input
              value={aiForm.keywords}
              onChange={(e) => setAiForm({ ...aiForm, keywords: e.target.value })}
              placeholder="sistema para açougue, PDV açougue, balança integrada"
              data-testid="blog-ai-input-keywords"
              disabled={generating}
            />
            <p className="text-xs text-slate-500 mt-1">
              Opcional, mas recomendado. A IA usa essas palavras naturalmente no texto.
            </p>
          </div>

          <div>
            <Label>Tamanho</Label>
            <select
              className="w-full mt-1 rounded-md border border-input bg-background p-2 text-sm"
              value={aiForm.length}
              onChange={(e) => setAiForm({ ...aiForm, length: e.target.value })}
              data-testid="blog-ai-input-length"
              disabled={generating}
            >
              <option value="curto">Curto (600-900 palavras)</option>
              <option value="medio">Médio (900-1300 palavras)</option>
              <option value="longo">Longo (1300-1800 palavras) — recomendado para SEO</option>
            </select>
          </div>

          <div className="flex justify-end gap-2 border-t pt-3">
            <Button
              variant="outline"
              onClick={() => setShowAiModal(false)}
              disabled={generating}
              data-testid="blog-ai-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleGenerateAi}
              disabled={generating}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="blog-ai-generate"
            >
              {generating ? (
                <>
                  <Sparkles className="w-4 h-4 mr-2 animate-pulse" />
                  Gerando... (até 30s)
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Gerar rascunho
                </>
              )}
            </Button>
          </div>
        </div>
      </SimpleModal>
    </div>
  );
}
